/**
 * The Fidèle card quick add (snippets/fidele-card.liquid, DECISIONS row 44): "+ Add to cart" on a product card adds
 * one pair in the finish the card shows (the collection's finish switch, else the first finish), then the cart
 * drawer opens, as Horizon's own add does (the same cart event, with the drawer's section in the answer).
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const root = () => window.Shopify?.routes?.root || '/';

async function quickAdd(button) {
  const white = document.documentElement.dataset.finish === 'White';
  const id = (white && button.dataset.white) || button.dataset.yellow;
  if (!id || button.classList.contains('fdl-is-loading')) return;
  button.classList.add('fdl-is-loading');

  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean);
  const body = new FormData();
  body.append('id', id);
  body.append('quantity', '1');
  if (sectionIds.length) {
    body.append('sections', sectionIds.join(','));
    body.append('sections_url', window.location.pathname);
  }

  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: id, quantity: 1 }],
      promise: deferred.promise,
    })
  );
  try {
    const response = await fetch(`${root()}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    });
    const added = await response.json();
    const ajaxCart = await (await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } })).json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        items: ajaxCart.items,
        source: 'fidele-quick-add',
        sourceId: 'fidele-quick-add',
        itemCount: 1,
        sections: added.sections,
        didError: Boolean(added.status),
      },
    });
    if (added.status) {
      document.dispatchEvent(new CartErrorEvent({ error: added.message || 'Add to cart failed', code: 'INVALID' }));
    }
  } catch (error) {
    deferred.reject(error);
  } finally {
    button.classList.remove('fdl-is-loading');
  }
}

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-fdl-quick-add]') : null;
  if (!button) return;
  event.preventDefault();
  event.stopPropagation();
  quickAdd(button);
});

/**
 * The gift's finish switch in the cart drawer (DECISIONS row 55): the gift line is replaced by the other finish,
 * then the drawer redraws through the same cart event.
 */
async function swapGift(button) {
  if (button.disabled || button.classList.contains('fdl-is-loading')) return;
  button.classList.add('fdl-is-loading');
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean);
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: [{ merchandiseId: button.dataset.variantId, quantity: 1 }],
      promise: deferred.promise,
    })
  );
  try {
    await fetch(`${root()}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: button.dataset.lineKey, quantity: 0 }),
    });
    const body = { items: [{ id: Number(button.dataset.variantId), quantity: 1, properties: { _stack_gift: '1' } }] };
    if (sectionIds.length) Object.assign(body, { sections: sectionIds.join(','), sections_url: window.location.pathname });
    const added = await (
      await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
    ).json();
    const ajaxCart = await (await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } })).json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: { items: ajaxCart.items, source: 'fidele-gift-swap', sourceId: 'fidele-gift-swap', sections: added.sections, didError: Boolean(added.status) },
    });
  } catch (error) {
    deferred.reject(error);
  } finally {
    button.classList.remove('fdl-is-loading');
  }
}

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-fdl-gift-swap]') : null;
  if (!button) return;
  event.preventDefault();
  swapGift(button);
});
